// FLEXX Academy — premium dark luxury Art Deco certificate.
// Pure React + inline SVG + inline styles. Zero PNG templates, zero Tailwind,
// zero external CSS. Fully dynamic per tier. Single source of truth used by:
//   • Tier 1 capstone cert (MasteryCapstone.jsx)
//   • Tier 2 / 3 / 4 cert modals
//   • Public /verify/:cert_id page
//   • Print / "Save as PDF" view (openCertificatePrintView)
//
// Prop API (matches the spec):
//   studentName        string
//   courseName         string
//   courseSubtitle     string
//   completionDate     string ("May 3, 2026")
//   verifyCode         string (certificate id / HMAC hash tail)
//   tier               "foundation" | "intermediate" | "ai" | "algo" | "quant"
//   onClose?           () => void
//   onDownload?        () => void  (PDF)
//   shareBaseUrl?      string (defaults to window.location.origin)
//   hideShare?         boolean (opt-out of share panel / PNG)

import React, { useEffect, useRef, useState } from "react";

// ── Tier config ────────────────────────────────────────────────────────
const TIER_CONFIG = {
  foundation:   { badge: "FOUNDATIONAL", accent: "#C9A227", glow: "rgba(201,162,39,0.40)" },
  intermediate: { badge: "INTERMEDIATE", accent: "#C9A227", glow: "rgba(201,162,39,0.40)" },
  ai:           { badge: "AI-POWERED",   accent: "#10b981", glow: "rgba(16,185,129,0.40)" },
  algo:         { badge: "ALGORITHMIC",  accent: "#3b82f6", glow: "rgba(59,130,246,0.40)" },
  quant:        { badge: "QUANTITATIVE", accent: "#a855f7", glow: "rgba(168,85,247,0.40)" },
};

const GOLD = "#C9A227";
const GOLD_LIGHT = "#E8C84A";
const GOLD_DARK = "#8B6914";

// Logo asset (static, offline-friendly) — served from /public
const LOGO_SRC = "/flexx-logo.png";

// ── Google Fonts loader (idempotent) ───────────────────────────────────
const FONTS_HREF = "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700;900&family=Cinzel+Decorative:wght@400;700;900&family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,600&family=Dancing+Script:wght@500;700&display=swap";
function useFlexxCertFonts() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("flexx-cert-fonts")) return;
    const l = document.createElement("link");
    l.id = "flexx-cert-fonts";
    l.rel = "stylesheet";
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }, []);
}

// ── Reusable SVG pieces ────────────────────────────────────────────────
// Art Deco corner ornament — ornate filigree (not just a chevron).
function CornerOrnament({ position }) {
  const transforms = {
    tl: "",
    tr: "scale(-1,1) translate(-90,0)",
    bl: "scale(1,-1) translate(0,-90)",
    br: "scale(-1,-1) translate(-90,-90)",
  };
  return (
    <svg width="90" height="90" viewBox="0 0 90 90" style={{ position: "absolute",
      top: position[0] === "t" ? 6 : "auto",
      bottom: position[0] === "b" ? 6 : "auto",
      left: position[1] === "l" ? 6 : "auto",
      right: position[1] === "r" ? 6 : "auto",
      pointerEvents: "none",
    }}>
      <defs>
        <linearGradient id={`cg-${position}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={GOLD_LIGHT} />
          <stop offset="0.5" stopColor={GOLD} />
          <stop offset="1" stopColor={GOLD_DARK} />
        </linearGradient>
      </defs>
      <g transform={transforms[position]} fill="none" stroke={`url(#cg-${position})`} strokeWidth="1.2" strokeLinecap="round">
        {/* Corner L-brackets — double line */}
        <path d="M 2 2 L 56 2" />
        <path d="M 2 2 L 2 56" />
        <path d="M 6 6 L 42 6" strokeWidth="0.7" opacity="0.85" />
        <path d="M 6 6 L 6 42" strokeWidth="0.7" opacity="0.85" />
        {/* Decorative flourish inside the corner */}
        <path d="M 14 14 Q 26 14 26 26 Q 26 38 14 38" />
        <path d="M 14 14 Q 14 26 26 26 Q 38 26 38 14" opacity="0.7" />
        {/* Center gem */}
        <path d="M 22 22 L 28 22 L 28 28 L 22 28 Z" fill={GOLD} stroke="none"
          transform="rotate(45 25 25)" />
        {/* Outer sunburst rays */}
        <path d="M 50 6 L 68 6" strokeWidth="0.8" />
        <path d="M 6 50 L 6 68" strokeWidth="0.8" />
        <path d="M 60 10 L 74 10" strokeWidth="0.5" opacity="0.7" />
        <path d="M 10 60 L 10 74" strokeWidth="0.5" opacity="0.7" />
        {/* Corner dots */}
        <circle cx="50" cy="12" r="1.1" fill={GOLD} stroke="none" />
        <circle cx="12" cy="50" r="1.1" fill={GOLD} stroke="none" />
        <circle cx="64" cy="16" r="0.8" fill={GOLD} stroke="none" opacity="0.8" />
        <circle cx="16" cy="64" r="0.8" fill={GOLD} stroke="none" opacity="0.8" />
      </g>
    </svg>
  );
}

function CandlestickBackground() {
  // 14 candles ascending trend. Alternating bullish filled / bearish outline.
  const candles = [
    [40, 230, 60, 80],   [75, 210, 75, 68],
    [110, 220, 55, 84],  [145, 200, 70, 70],
    [180, 210, 48, 85],  [215, 190, 62, 72],
    [250, 200, 42, 82],  [285, 180, 58, 70],
    [320, 195, 40, 80],  [355, 170, 55, 68],
    [390, 185, 36, 78],  [425, 158, 52, 66],
    [460, 175, 32, 74],  [495, 148, 50, 62],
  ];
  return (
    <svg width="100%" height="100%" viewBox="0 0 560 300" preserveAspectRatio="xMidYMid slice"
      style={{ position: "absolute", inset: 0, opacity: 0.06, pointerEvents: "none" }}>
      {candles.map(([x, wickTop, wickH, bodyH], i) => {
        const bullish = i % 2 === 0;
        const bodyY = wickTop + (wickH - bodyH) / 2;
        return (
          <g key={i} stroke={GOLD} strokeWidth="1.2" fill={bullish ? GOLD : "none"}>
            <line x1={x + 7} y1={wickTop} x2={x + 7} y2={wickTop + wickH} />
            <rect x={x} y={bodyY} width="14" height={bodyH} />
          </g>
        );
      })}
      {/* Ascending trend line */}
      <path d="M 20 250 Q 140 200 280 150 T 540 40" stroke={GOLD} strokeWidth="1.5" fill="none" opacity="0.55" />
      <path d="M 520 55 L 545 35 L 525 30 Z" fill={GOLD} opacity="0.55" />
    </svg>
  );
}

function TrendIcon({ size = 24, color = GOLD }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17 L9 11 L13 15 L21 6" />
      <path d="M14 6 L21 6 L21 13" />
    </svg>
  );
}

function FlexxBadge({ logoSrc = LOGO_SRC }) {
  return (
    <div style={{ position: "relative", width: 280, height: 112, margin: "0 auto",
      filter: `drop-shadow(0 6px 22px ${GOLD_DARK}88)` }}>
      <svg viewBox="0 0 280 112" width="100%" height="100%" preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0 }}>
        <defs>
          <linearGradient id="flexxBadgeBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1a1507" />
            <stop offset="0.6" stopColor="#0d0a04" />
            <stop offset="1" stopColor="#0a0a0a" />
          </linearGradient>
          <linearGradient id="flexxBadgeStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={GOLD_LIGHT} />
            <stop offset="0.5" stopColor={GOLD} />
            <stop offset="1" stopColor={GOLD_DARK} />
          </linearGradient>
        </defs>
        <path d="M 28 10 L 252 10 L 272 56 L 252 102 L 28 102 L 8 56 Z"
          fill="url(#flexxBadgeBg)" stroke="url(#flexxBadgeStroke)" strokeWidth="2" />
        <path d="M 32 14 L 248 14 L 266 56 L 248 98 L 32 98 L 14 56 Z"
          fill="none" stroke={GOLD_DARK} strokeWidth="0.7" opacity="0.9" />
        <circle cx="16" cy="56" r="1.8" fill={GOLD} />
        <circle cx="264" cy="56" r="1.8" fill={GOLD} />
      </svg>
      <img src={logoSrc} alt="FLEXX Academy" crossOrigin="anonymous"
        style={{ position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", height: 76, maxWidth: 230,
          objectFit: "contain", pointerEvents: "none" }} />
    </div>
  );
}

function DecorativeDivider({ color = GOLD, width = 220 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "8px auto" }}>
      <span style={{ width: width / 2 - 16, height: 1, background: `linear-gradient(90deg, transparent, ${color}88, ${color})` }} />
      <svg width="14" height="14" viewBox="0 0 14 14">
        <path d="M 7 1 L 13 7 L 7 13 L 1 7 Z" fill={color} stroke={color} strokeWidth="0.8" opacity="0.95" />
      </svg>
      <span style={{ width: width / 2 - 16, height: 1, background: `linear-gradient(-90deg, transparent, ${color}88, ${color})` }} />
    </div>
  );
}

function Seal({ size = 96, logoSrc = LOGO_SRC }) {
  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto",
      filter: `drop-shadow(0 0 14px ${GOLD_DARK}cc)` }}>
      <svg width={size} height={size} viewBox="0 0 96 96">
        <defs>
          <radialGradient id="sealBg" cx="0.5" cy="0.4" r="0.6">
            <stop offset="0" stopColor="#2a2008" />
            <stop offset="1" stopColor="#0a0a0a" />
          </radialGradient>
          <linearGradient id="sealRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={GOLD_LIGHT} />
            <stop offset="0.5" stopColor={GOLD} />
            <stop offset="1" stopColor={GOLD_DARK} />
          </linearGradient>
        </defs>
        {/* Outer scalloped ring */}
        {Array.from({ length: 32 }).map((_, i) => {
          const a = (i * 360) / 32;
          const rad = (a * Math.PI) / 180;
          const x1 = 48 + Math.cos(rad) * 44;
          const y1 = 48 + Math.sin(rad) * 44;
          const x2 = 48 + Math.cos(rad) * 47;
          const y2 = 48 + Math.sin(rad) * 47;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={GOLD} strokeWidth="1" opacity="0.75" />;
        })}
        {/* Main ring */}
        <circle cx="48" cy="48" r="44" fill="url(#sealBg)" stroke="url(#sealRing)" strokeWidth="2.2" />
        <circle cx="48" cy="48" r="40" fill="none" stroke={GOLD_DARK} strokeWidth="0.6" />
        <circle cx="48" cy="48" r="36" fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="1 2" opacity="0.7" />
        {/* Inner dotted ring */}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * 360) / 24;
          const rad = (a * Math.PI) / 180;
          const x = 48 + Math.cos(rad) * 32;
          const y = 48 + Math.sin(rad) * 32;
          return <circle key={i} cx={x} cy={y} r="0.6" fill={GOLD} opacity="0.7" />;
        })}
      </svg>
      {/* Real logo centred in the seal */}
      <img src={logoSrc} alt="" crossOrigin="anonymous"
        style={{ position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          height: size * 0.5, maxWidth: size * 0.72,
          objectFit: "contain", pointerEvents: "none",
        }} />
    </div>
  );
}

// Gold corner tick inside framed boxes — pairs at each corner give the
// name/course boxes that engraved "Art Deco plaque" feeling.
function BoxCornerTick({ corner }) {
  const pos = {
    tl: { top: -1, left: -1, transform: "rotate(0deg)" },
    tr: { top: -1, right: -1, transform: "rotate(90deg)" },
    br: { bottom: -1, right: -1, transform: "rotate(180deg)" },
    bl: { bottom: -1, left: -1, transform: "rotate(270deg)" },
  }[corner];
  return (
    <svg width="14" height="14" viewBox="0 0 14 14"
      style={{ position: "absolute", pointerEvents: "none", ...pos }}>
      <path d="M 0 0 L 14 0 L 14 2 L 2 2 L 2 14 L 0 14 Z" fill={GOLD} />
    </svg>
  );
}

// Framed plaque used for the student name + course title — dark bg, thin gold
// border, tiny gold tick in each corner.
function Plaque({ children, accent = GOLD, padding = "14px 22px", maxWidth = 560, testid }) {
  return (
    <div data-testid={testid} style={{
      position: "relative", margin: "0 auto", maxWidth, padding,
      background: "rgba(0,0,0,0.55)",
      border: `1px solid ${accent}66`,
      boxShadow: `inset 0 0 0 1px #00000066, 0 0 14px ${accent}15`,
    }}>
      <BoxCornerTick corner="tl" />
      <BoxCornerTick corner="tr" />
      <BoxCornerTick corner="br" />
      <BoxCornerTick corner="bl" />
      {children}
    </div>
  );
}
export function FlexxCertificate({
  studentName = "Felix Adiova",
  courseName = "Core Principles of Financial Markets & Trading Strategy",
  courseSubtitle = "A foundational component of the FLEXX ACADEMY Elite Trading Series",
  completionDate = "May 3, 2026",
  verifyCode = "",
  tier = "foundation",
  onClose = null,
  onDownload = null,
  shareBaseUrl = null,
  hideShare = false,
}) {
  useFlexxCertFonts();
  const config = TIER_CONFIG[tier] || TIER_CONFIG.foundation;
  const certRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const baseOrigin = shareBaseUrl || (typeof window !== "undefined" ? window.location.origin : "");
  const shareUrl = verifyCode ? `${baseOrigin}/verify/${verifyCode}` : baseOrigin;
  const shareText = `I just earned the ${config.badge} certificate from FLEXX ACADEMY — ${courseName}. Verify it here:`;

  // Preload the logo and strip its dark rectangular background so it becomes a
  // genuinely transparent-bg PNG. This makes it render correctly EVERYWHERE:
  // live DOM, html-to-image PNG export, and the print-view PDF. mix-blend-mode
  // is unreliable across those three contexts (particularly iOS Safari + SVG
  // foreignObject), so we solve it at the pixel level once and reuse everywhere.
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 400;
        canvas.height = img.naturalHeight || 400;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const pix = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = pix.data;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          // Dark (<42) → transparent; mid (42–95) → fade out smoothly.
          if (lum < 42) {
            d[i + 3] = 0;
          } else if (lum < 95) {
            d[i + 3] = Math.floor(d[i + 3] * ((lum - 42) / 53));
          }
        }
        ctx.putImageData(pix, 0, 0);
        if (!cancelled) setLogoDataUrl(canvas.toDataURL("image/png"));
      } catch (e) {
        if (!cancelled) setLogoDataUrl(null); // silent fallback to LOGO_SRC
      }
    };
    img.onerror = () => { /* network / 404 — keep fallback */ };
    img.src = LOGO_SRC;
    return () => { cancelled = true; };
  }, []);

  const openShare = (href) => {
    if (typeof window !== "undefined") {
      window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
    }
  };
  const shareLinkedIn = () => openShare(
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
  );
  const shareTwitter = () => openShare(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
  );
  const shareWhatsApp = () => openShare(
    `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + " " + shareUrl)}`
  );
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (__) {}
      document.body.removeChild(ta);
    }
  };

  const overlay = {
    position: "fixed", inset: 0, zIndex: 9800,
    background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    overflowY: "auto",
  };
  const frame = {
    position: "relative", width: "100%", maxWidth: 900,
    background: "linear-gradient(180deg, #141208 0%, #0a0a0a 60%, #050505 100%)",
    border: `3px solid ${GOLD}`,
    outline: `1px solid ${GOLD_DARK}`,
    outlineOffset: 6,
    boxShadow: `0 0 100px ${config.glow}, 0 0 28px rgba(0,0,0,0.95) inset, 0 24px 72px rgba(0,0,0,0.8)`,
    padding: 38,
  };
  const innerFrame = {
    position: "relative",
    border: `1px solid ${GOLD_DARK}`,
    outline: `1px solid ${GOLD}33`,
    outlineOffset: 4,
    padding: "46px 50px 36px",
    minHeight: 620,
  };

  const withClose = typeof onClose === "function";
  const wrapper = withClose ? overlay : { position: "relative", padding: "20px 0" };

  return (
    <div data-testid="flexx-certificate-overlay" style={wrapper}>
      <div ref={certRef} data-testid="flexx-certificate" style={frame}>
        {/* Background candlesticks */}
        <CandlestickBackground />

        {/* 4 corner ornaments */}
        <CornerOrnament position="tl" />
        <CornerOrnament position="tr" />
        <CornerOrnament position="bl" />
        <CornerOrnament position="br" />

        {/* Edge midpoint dots */}
        <span style={edgeDot("top")} /><span style={edgeDot("bottom")} />
        <span style={edgeDot("left")} /><span style={edgeDot("right")} />

        {/* Vertical decorative bars left + right */}
        <span style={sideBar("left")} />
        <span style={sideBar("right")} />

        <div style={innerFrame}>
          {/* Badge */}
          <div style={{ position: "relative", zIndex: 2, marginBottom: 14 }}>
            <FlexxBadge logoSrc={logoDataUrl || LOGO_SRC} />
          </div>

          {/* Title */}
          <div style={{ textAlign: "center", fontFamily: "'Cinzel', serif", fontWeight: 700,
            fontSize: 17, letterSpacing: 9, color: GOLD,
            textShadow: `0 0 22px ${GOLD}55`, marginTop: 22 }}>
            CERTIFICATE OF COMPLETION
          </div>
          <DecorativeDivider />

          {/* Student name plaque */}
          <Plaque testid="flexx-cert-name-box" accent={GOLD} padding="26px 36px" maxWidth={660}>
            <div style={{
              textAlign: "center",
              fontFamily: "'Great Vibes', 'Cormorant Garamond', serif",
              fontSize: "clamp(38px, 5.4vw, 54px)",
              color: GOLD_LIGHT, lineHeight: 1.1, letterSpacing: 0.5,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              padding: "6px 0 10px",
              textShadow: `0 0 22px ${GOLD}77, 0 2px 6px rgba(0,0,0,0.85)`,
              background: `linear-gradient(180deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, ${GOLD_DARK} 100%)`,
              WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              {studentName}
            </div>
          </Plaque>

          {/* Body line */}
          <div style={{ textAlign: "center", fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic", fontSize: 15, letterSpacing: 2, color: "#c8bfa0",
            margin: "22px 0 22px" }}>
            has successfully completed the comprehensive educational module
          </div>

          {/* Course name plaque */}
          <Plaque testid="flexx-cert-course-box" accent={GOLD} padding="20px 28px" maxWidth={720}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, justifyContent: "center" }}>
              <TrendIcon size={28} />
              <div style={{
                flex: "0 1 auto", textAlign: "center",
                fontFamily: "'Cinzel', serif", fontWeight: 700,
                fontSize: 20, color: GOLD_LIGHT, letterSpacing: 0.8, lineHeight: 1.3,
                textShadow: `0 0 14px ${GOLD}55`,
              }}>
                {courseName}
              </div>
              <TrendIcon size={28} />
            </div>
          </Plaque>

          {/* Subtitle */}
          <div style={{ textAlign: "center", fontFamily: "'Cinzel', serif",
            fontSize: 10, letterSpacing: 4, color: `${GOLD}dd`, fontStyle: "italic",
            textTransform: "uppercase", marginTop: 14, marginBottom: 34,
          }}>
            {courseSubtitle}
          </div>

          {/* Bottom row: Date | Seal | Signature */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr",
            alignItems: "end", gap: 24, marginTop: 22 }}>
            {/* Date */}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: 9,
                letterSpacing: 3.5, color: "#998b6a", marginBottom: 10 }}>
                DATE OF COMPLETION
              </div>
              <div style={{ borderTop: `1px solid ${GOLD}77`, paddingTop: 8 }}>
                <div data-testid="flexx-cert-date" style={{
                  fontFamily: "'Cinzel', serif", fontWeight: 600, fontSize: 13,
                  color: GOLD_LIGHT, letterSpacing: 1.2,
                }}>{completionDate}</div>
              </div>
            </div>

            {/* Seal */}
            <div><Seal logoSrc={logoDataUrl || LOGO_SRC} /></div>

            {/* Signature */}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700,
                fontSize: 30, color: GOLD_LIGHT, lineHeight: 1,
                textShadow: `0 1px 2px rgba(0,0,0,0.6), 0 0 8px ${GOLD}44`,
                transform: "rotate(-2deg)",
              }}>
                Felix Adiova
              </div>
              <div style={{ borderTop: `1px solid ${GOLD}77`, marginTop: 8, paddingTop: 8 }}>
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: 8,
                  letterSpacing: 2.5, color: "#998b6a", fontWeight: 600 }}>
                  FOUNDER / CEO · FLEXX ACADEMY
                </div>
              </div>
            </div>
          </div>

          {/* Footer: verify + pill */}
          <div style={{ marginTop: 26, paddingTop: 14,
            borderTop: `1px solid ${GOLD}33`,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div data-testid="flexx-cert-verify-code" style={{
              fontFamily: "'Courier New', monospace", fontSize: 9, letterSpacing: 1.5,
              color: "#555",
            }}>
              VERIFY — {verifyCode || "LOCAL"}
            </div>
            <div style={{
              fontFamily: "'Cinzel', serif", fontSize: 8, letterSpacing: 3,
              color: config.accent, padding: "4px 12px", borderRadius: 999,
              border: `1px solid ${config.accent}88`, background: `${config.accent}12`,
            }}>
              ✓ CRYPTOGRAPHICALLY VERIFIED · {config.badge} CERTIFIED
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {(onDownload || onClose || !hideShare) && (
        <div style={{ position: withClose ? "fixed" : "static",
          bottom: withClose ? 24 : "auto", left: 0, right: 0,
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 10, marginTop: withClose ? 0 : 18 }}>
          {!hideShare && (
            <div data-testid="flexx-cert-share-row"
              style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8,
                padding: "8px 14px", background: "rgba(10,10,10,0.85)",
                border: `1px solid ${config.accent}55`, borderRadius: 4,
                backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: 9, letterSpacing: 3,
                color: config.accent, alignSelf: "center", paddingRight: 4, textTransform: "uppercase" }}>
                Share
              </span>
              <button data-testid="flexx-cert-share-linkedin" onClick={shareLinkedIn}
                title="Share on LinkedIn" style={btnSocial(config.accent)}>in</button>
              <button data-testid="flexx-cert-share-x" onClick={shareTwitter}
                title="Share on X / Twitter" style={btnSocial(config.accent)}>𝕏</button>
              <button data-testid="flexx-cert-share-whatsapp" onClick={shareWhatsApp}
                title="Share on WhatsApp" style={btnSocial(config.accent)}>✆</button>
              <button data-testid="flexx-cert-share-copy" onClick={copyLink}
                title="Copy verification link" style={btnSocial(config.accent, true)}>
                {copied ? "✓ COPIED" : "COPY LINK"}
              </button>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
            {onDownload && (
              <button data-testid="flexx-cert-download-btn" onClick={onDownload}
                style={btnPrimary(config.accent)}>
                ⬇ DOWNLOAD PDF
              </button>
            )}
            {onClose && (
              <button data-testid="flexx-cert-close-btn" onClick={onClose}
                style={btnGhost(config.accent)}>
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline style helpers ──────────────────────────────────────────────
function edgeDot(edge) {
  const common = { position: "absolute", width: 6, height: 6, borderRadius: "50%",
    background: GOLD, boxShadow: `0 0 6px ${GOLD}88`, pointerEvents: "none" };
  if (edge === "top")    return { ...common, top: -3, left: "50%", transform: "translateX(-50%)" };
  if (edge === "bottom") return { ...common, bottom: -3, left: "50%", transform: "translateX(-50%)" };
  if (edge === "left")   return { ...common, left: -3, top: "50%", transform: "translateY(-50%)" };
  return                      { ...common, right: -3, top: "50%", transform: "translateY(-50%)" };
}
function sideBar(side) {
  return {
    position: "absolute", top: "15%", bottom: "15%", width: 1,
    background: `linear-gradient(180deg, transparent, ${GOLD}aa 20%, ${GOLD}aa 80%, transparent)`,
    [side]: 14, pointerEvents: "none",
  };
}
function btnPrimary(color) {
  return {
    fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: 2,
    fontSize: 11, padding: "10px 22px", cursor: "pointer",
    background: color, color: "#0a0a0a",
    border: `1px solid ${color}`, borderRadius: 4,
  };
}
function btnGhost(color) {
  return {
    fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: 2,
    fontSize: 11, padding: "10px 22px", cursor: "pointer",
    background: "transparent", color,
    border: `1px solid ${color}88`, borderRadius: 4,
  };
}
function btnSocial(color, wide = false) {
  return {
    fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: wide ? 2 : 0,
    fontSize: wide ? 9 : 13,
    width: wide ? "auto" : 30, height: 30,
    padding: wide ? "0 12px" : 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "transparent", color,
    border: `1px solid ${color}aa`, borderRadius: 4,
    cursor: "pointer",
    transition: "background-color 150ms ease, color 150ms ease",
  };
}

// ── Legacy helpers kept for existing callers ──────────────────────────
// (other tier modals previously imported openCertificatePrintView)

// Integer-tier → string-tier mapper for upstream call-sites that still
// speak in tier numbers (1/2/3/4/5).
export const TIER_NAME_BY_NUMBER = {
  1: "foundation", 2: "intermediate", 3: "ai", 4: "algo", 5: "quant",
};

export const TIER_COURSE_COPY = {
  foundation: {
    courseName: "Core Principles of Financial Markets & Trading Strategy",
    courseSubtitle: "A foundational component of the FLEXX ACADEMY Elite Trading Series",
  },
  intermediate: {
    courseName: "The SuPER System — Pro Trading Mechanics",
    courseSubtitle: "An intermediate component of the FLEXX ACADEMY Elite Trading Series",
  },
  ai: {
    courseName: "AI-Powered Trading — SageMaster & Helix",
    courseSubtitle: "An advanced component of the FLEXX ACADEMY Elite Trading Series",
  },
  algo: {
    courseName: "Algorithmic Trading — MT5 & Expert Advisors",
    courseSubtitle: "The capstone of the FLEXX ACADEMY Elite Trading Series",
  },
  quant: {
    courseName: "Quantitative Trading — Python & Statistical Edge",
    courseSubtitle: "The pinnacle of the FLEXX ACADEMY Elite Trading Series",
  },
};

// Opens the same certificate design in a new window sized for A4 landscape
// print. Re-uses the exact React markup via ReactDOMServer-less approach:
// we emit the resolved HTML string by serialising inline styles ourselves
// so no extra dependency is needed. A simple approach: open a window that
// renders the FlexxCertificate via an inline script. Simpler still: write
// a self-contained HTML clone matching the same visual design.
export function openCertificatePrintView({
  studentName, courseName, courseSubtitle, completionDate, verifyCode, tier = "foundation",
  logoDataUrl = null,
}) {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.foundation;
  const w = window.open("", "_blank", "width=1100,height=900");
  if (!w) return;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Prefer the caller-supplied transparent data URL; fall back to the raw PNG
  // (the inline JS below will strip the dark bg at load time as defence).
  const logoUrl = logoDataUrl || (origin + LOGO_SRC);
  const html = `<!doctype html><html><head><meta charset="utf-8" />
<title>FLEXX Academy Certificate — ${escapeHtml(studentName || "FLEXX Trader")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="${FONTS_HREF}" rel="stylesheet" />
<style>
  @page { size: A4 landscape; margin: 10mm; }
  *,*::before,*::after { box-sizing: border-box; }
  body { margin: 0; padding: 24px; background: #0a0a0a; color: #e8e8e8; font-family: 'Cinzel', serif; }
  .frame { position: relative; max-width: 960px; margin: 0 auto; background: linear-gradient(180deg,#141208 0%,#0a0a0a 60%, #050505 100%); border: 2px solid ${GOLD}; outline: 1px solid ${GOLD_DARK}; outline-offset: 4px; box-shadow: 0 0 80px ${config.glow}, 0 20px 60px rgba(0,0,0,0.7); padding: 32px; }
  .inner { position: relative; border: 1px solid ${GOLD_DARK}; outline: 1px solid ${GOLD}22; outline-offset: 3px; padding: 40px 44px 32px; min-height: 560px; }
  .badge-wrap { text-align: center; margin: 4px 0 18px; }
  .badge { position: relative; display: inline-block; width: 260px; height: 100px; filter: drop-shadow(0 4px 18px ${GOLD_DARK}66); }
  .badge svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .badge img { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); height: 66px; max-width: 210px; object-fit: contain; }
  .title { text-align: center; font-family: 'Cinzel', serif; font-weight: 700; font-size: 14px; letter-spacing: 7px; color: ${GOLD}; text-shadow: 0 0 18px ${GOLD}44; margin-top: 18px; }
  .div { display: flex; align-items: center; justify-content: center; gap: 10px; margin: 12px auto; }
  .div .dot { width: 12px; height: 12px; border-radius: 50%; background: ${GOLD}; opacity: 0.55; }
  .div .line { width: 150px; height: 1px; background: linear-gradient(90deg, transparent, ${GOLD}55 10%, ${GOLD}); }
  .div .line.r { background: linear-gradient(-90deg, transparent, ${GOLD}55 10%, ${GOLD}); }
  .div .diamond { width: 22px; height: 22px; position: relative; display: flex; align-items: center; justify-content: center; }
  .plaque { position: relative; margin: 0 auto; background: rgba(0,0,0,0.55); border: 1px solid ${GOLD}66; box-shadow: inset 0 0 0 1px #00000066, 0 0 14px ${GOLD}15; }
  .plaque::before, .plaque::after, .plaque > .tick-bl, .plaque > .tick-br { content: ""; position: absolute; width: 14px; height: 14px; background: ${GOLD}; clip-path: polygon(0 0, 100% 0, 100% 14%, 14% 14%, 14% 100%, 0 100%); }
  .plaque::before { top: -1px; left: -1px; }
  .plaque::after { top: -1px; right: -1px; transform: scaleX(-1); }
  .plaque .tick-bl { bottom: -1px; left: -1px; transform: scaleY(-1); }
  .plaque .tick-br { bottom: -1px; right: -1px; transform: scale(-1,-1); }
  .name-box { max-width: 620px; padding: 18px 28px; margin-top: 4px; }
  .name { text-align: center; font-family: 'Great Vibes', 'Cormorant Garamond', serif; font-size: 78px; color: ${GOLD_LIGHT}; letter-spacing: 1px; line-height: 1; padding: 8px 0 16px; text-shadow: 0 0 18px ${GOLD}66, 0 2px 4px rgba(0,0,0,0.8); background: linear-gradient(180deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, ${GOLD_DARK} 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
  .body-line { text-align: center; font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 13px; letter-spacing: 1.8px; color: #b8b090; margin: 16px 0 18px; }
  .course-box { max-width: 700px; padding: 16px 22px; display: flex; align-items: center; gap: 16px; justify-content: center; }
  .course { font-family: 'Cinzel', serif; font-weight: 700; font-size: 17px; color: ${GOLD_LIGHT}; letter-spacing: 0.6px; line-height: 1.3; text-align: center; text-shadow: 0 0 12px ${GOLD}44; }
  .subtitle { text-align: center; font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 3.5px; color: ${GOLD}dd; font-style: italic; text-transform: uppercase; margin: 12px 0 30px; }
  .row { display: grid; grid-template-columns: 1fr auto 1fr; align-items: end; gap: 24px; margin-top: 22px; }
  .lab { font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 3.5px; color: #998b6a; margin-bottom: 10px; }
  .date-val { font-family: 'Cinzel', serif; font-weight: 600; font-size: 13px; color: ${GOLD_LIGHT}; letter-spacing: 1.2px; border-top: 1px solid ${GOLD}77; padding-top: 8px; }
  .sig { font-family: 'Dancing Script', cursive; font-weight: 700; font-size: 32px; color: ${GOLD_LIGHT}; line-height: 1; text-shadow: 0 1px 2px rgba(0,0,0,0.6), 0 0 8px ${GOLD}44; transform: rotate(-2deg); }
  .sig-role { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 2.5px; color: #998b6a; font-weight: 600; border-top: 1px solid ${GOLD}77; margin-top: 8px; padding-top: 8px; }
  .seal-box { position: relative; width: 98px; height: 98px; margin: 0 auto; filter: drop-shadow(0 0 14px ${GOLD_DARK}cc); }
  .seal-box img { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); height: 44px; max-width: 70px; object-fit: contain; }
  .footer { margin-top: 30px; padding-top: 16px; border-top: 1px solid ${GOLD}33; display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
  .verify { font-family: 'Courier New', monospace; font-size: 9px; letter-spacing: 1.5px; color: #555; }
  .pill { font-family: 'Cinzel', serif; font-weight: 700; font-size: 9px; letter-spacing: 3px; color: ${config.accent}; padding: 5px 14px; border-radius: 999px; border: 1px solid ${config.accent}88; background: ${config.accent}12; }
  .toolbar { position: fixed; top: 14px; right: 14px; display: flex; gap: 8px; z-index: 10; }
  .toolbar button { padding: 9px 16px; font-family: 'Cinzel', serif; font-weight: 700; letter-spacing: 2px; font-size: 11px; border: 1px solid ${GOLD}; background: ${GOLD}; color: #0a0a0a; border-radius: 4px; cursor: pointer; }
  .toolbar button.secondary { background: transparent; color: ${GOLD}; }
  @media print { .toolbar { display: none !important; } body { background: #fff; padding: 0; } }
</style></head><body>
<div class="toolbar">
  <button onclick="window.print()">⬇ SAVE / PRINT AS PDF</button>
  <button class="secondary" onclick="window.close()">CLOSE</button>
</div>
<div class="frame">
  <div class="inner">
    <div class="badge-wrap"><div class="badge">
      <svg viewBox="0 0 260 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bbg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1a1507"/><stop offset="0.6" stop-color="#0d0a04"/><stop offset="1" stop-color="#0a0a0a"/></linearGradient>
          <linearGradient id="bst" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${GOLD_LIGHT}"/><stop offset="0.5" stop-color="${GOLD}"/><stop offset="1" stop-color="${GOLD_DARK}"/></linearGradient>
        </defs>
        <path d="M 26 8 L 234 8 L 252 50 L 234 92 L 26 92 L 8 50 Z" fill="url(#bbg)" stroke="url(#bst)" stroke-width="1.6"/>
        <path d="M 30 12 L 230 12 L 246 50 L 230 88 L 30 88 L 14 50 Z" fill="none" stroke="${GOLD_DARK}" stroke-width="0.6" opacity="0.9"/>
        <circle cx="16" cy="50" r="1.6" fill="${GOLD}"/><circle cx="244" cy="50" r="1.6" fill="${GOLD}"/>
      </svg>
      <img id="flexx-logo-badge" src="${logoUrl}" alt="FLEXX Academy" crossorigin="anonymous"/>
    </div></div>
    <div class="title">CERTIFICATE OF COMPLETION</div>
    <div class="div"><span class="dot"></span><span class="line"></span>
      <span class="diamond">
        <svg width="22" height="22" viewBox="0 0 22 22"><path d="M 11 2 L 20 11 L 11 20 L 2 11 Z" fill="none" stroke="${GOLD}" stroke-width="1" opacity="0.9"/><path d="M 11 5 L 17 11 L 11 17 L 5 11 Z" fill="${GOLD}"/><circle cx="11" cy="11" r="1.4" fill="#0a0a0a"/></svg>
      </span>
      <span class="line r"></span><span class="dot"></span></div>
    <div class="plaque name-box"><div class="tick-bl"></div><div class="tick-br"></div><div class="name">${escapeHtml(studentName || "FLEXX Trader")}</div></div>
    <div class="body-line">has successfully completed the comprehensive educational module</div>
    <div class="plaque course-box"><div class="tick-bl"></div><div class="tick-br"></div>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${GOLD}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17 L9 11 L13 15 L21 6"/><path d="M14 6 L21 6 L21 13"/></svg>
      <div class="course">${escapeHtml(courseName || "")}</div>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${GOLD}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17 L9 11 L13 15 L21 6"/><path d="M14 6 L21 6 L21 13"/></svg>
    </div>
    <div class="subtitle">${escapeHtml(courseSubtitle || "")}</div>
    <div class="row">
      <div style="text-align:center;"><div class="lab">DATE OF COMPLETION</div><div class="date-val">${escapeHtml(completionDate || "—")}</div></div>
      <div class="seal-box">
        <svg viewBox="0 0 98 98" width="98" height="98">
          <defs>
            <radialGradient id="sbg" cx="0.5" cy="0.4" r="0.6"><stop offset="0" stop-color="#2a2008"/><stop offset="1" stop-color="#0a0a0a"/></radialGradient>
            <linearGradient id="srg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${GOLD_LIGHT}"/><stop offset="0.5" stop-color="${GOLD}"/><stop offset="1" stop-color="${GOLD_DARK}"/></linearGradient>
          </defs>
          <circle cx="49" cy="49" r="45" fill="url(#sbg)" stroke="url(#srg)" stroke-width="2.2"/>
          <circle cx="49" cy="49" r="41" fill="none" stroke="${GOLD_DARK}" stroke-width="0.6"/>
          <circle cx="49" cy="49" r="37" fill="none" stroke="${GOLD}" stroke-width="0.4" stroke-dasharray="1 2" opacity="0.7"/>
        </svg>
        <img class="flexx-logo-seal" src="${logoUrl}" alt="" crossorigin="anonymous"/>
      </div>
      <div style="text-align:center;"><div class="sig">Felix Adiova</div><div class="sig-role">FOUNDER / CEO · FLEXX ACADEMY</div></div>
    </div>
    <div class="footer">
      <div class="verify">VERIFY — ${escapeHtml(verifyCode || "LOCAL")}</div>
      <div class="pill">✓ CRYPTOGRAPHICALLY VERIFIED · ${config.badge} CERTIFIED</div>
    </div>
  </div>
</div>
<script>
/* Auto-strip the logo's dark background so it blends into the gold frames.
   Runs AFTER the image loads. If the caller already passed a pre-processed
   transparent data URL, the second pass is a no-op. */
(function () {
  function process(imgEl) {
    if (!imgEl) return;
    if (imgEl.src.startsWith("data:image/png") && imgEl.dataset.flexxProcessed) return;
    var tryProcess = function () {
      try {
        var c = document.createElement("canvas");
        c.width = imgEl.naturalWidth || 400;
        c.height = imgEl.naturalHeight || 400;
        var ctx = c.getContext("2d");
        ctx.drawImage(imgEl, 0, 0);
        var id = ctx.getImageData(0, 0, c.width, c.height);
        var d = id.data;
        for (var i = 0; i < d.length; i += 4) {
          var lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          if (lum < 42) d[i + 3] = 0;
          else if (lum < 95) d[i + 3] = Math.floor(d[i + 3] * ((lum - 42) / 53));
        }
        ctx.putImageData(id, 0, 0);
        imgEl.src = c.toDataURL("image/png");
        imgEl.dataset.flexxProcessed = "1";
      } catch (e) { /* CORS or other — leave as-is */ }
    };
    if (imgEl.complete && imgEl.naturalWidth > 0) tryProcess();
    else imgEl.addEventListener("load", tryProcess, { once: true });
  }
  process(document.getElementById("flexx-logo-badge"));
  Array.prototype.forEach.call(document.querySelectorAll(".flexx-logo-seal"), process);
})();
</script>
</body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

// ── Demo wrapper (for isolated manual QA of all 5 tier variants) ──────
export default function CertificateDemo() {
  const [tier, setTier] = useState("foundation");
  const [show, setShow] = useState(true);
  const presets = {
    foundation:   { studentName: "Felix Adiova", ...TIER_COURSE_COPY.foundation },
    intermediate: { studentName: "Maria Hernandez", ...TIER_COURSE_COPY.intermediate },
    ai:           { studentName: "Aarav Patel", ...TIER_COURSE_COPY.ai },
    algo:         { studentName: "Yuki Tanaka", ...TIER_COURSE_COPY.algo },
    quant:        { studentName: "Dr. Sofia Reyes", ...TIER_COURSE_COPY.quant },
  };
  const data = presets[tier];
  if (!show) {
    return (
      <div style={{ padding: 40, textAlign: "center", background: "#0a0a0a", minHeight: "100vh", color: "#ccc" }}>
        <div style={{ marginBottom: 16 }}>Certificate hidden</div>
        <button onClick={() => setShow(true)} style={btnPrimary(GOLD)}>Show Certificate</button>
      </div>
    );
  }
  return (
    <>
      <div style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
        zIndex: 9999, display: "flex", gap: 6, padding: 6, background: "rgba(0,0,0,0.7)", borderRadius: 6 }}>
        {Object.keys(presets).map((t) => (
          <button key={t} onClick={() => setTier(t)} data-testid={`demo-tier-${t}`}
            style={{
              background: tier === t ? GOLD : "#222",
              color: tier === t ? "#000" : "#aaa",
              border: `1px solid ${tier === t ? GOLD : "#444"}`, borderRadius: 3,
              padding: "4px 10px", cursor: "pointer", fontSize: 10,
              fontFamily: "'Cinzel', serif", textTransform: "uppercase", letterSpacing: 2,
            }}>{t}</button>
        ))}
      </div>
      <FlexxCertificate
        studentName={data.studentName}
        courseName={data.courseName}
        courseSubtitle={data.courseSubtitle}
        completionDate="May 3, 2026"
        verifyCode="Zq9VKE1j_q22836vnTe0w"
        tier={tier}
        onClose={() => setShow(false)}
        onDownload={() => openCertificatePrintView({
          studentName: data.studentName, courseName: data.courseName,
          courseSubtitle: data.courseSubtitle, completionDate: "May 3, 2026",
          verifyCode: "Zq9VKE1j_q22836vnTe0w", tier,
        })}
      />
    </>
  );
}
